import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Download, Play, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import Layout from "@/components/Layout";
import Header from "@/components/Header";
import PageTransition from "@/components/PageTransition";
import ActionButton from "@/components/ActionButton";
import VideoModal from "@/components/VideoModal";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useIsMobile } from "@/hooks/use-mobile";
import { GET_USER, MARK_VIDEO_AS_DOWNLOADED } from "@/integrations/apollo/operations";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useSelector, useDispatch } from "react-redux";
import { setVideos } from "@/store/features/dashboardVideosSlice";
import { RootState } from "@/store";
import { Headset, Video } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Status mapping for styling
const statusLabels = {
  processing: "Processing",
  completed: "Ready to Download",
  draft: "Draft",
  rendering: "Rendering...",
  failed: "Failed"
};

const statusColors = {
  processing: "bg-blue-500",
  completed: "bg-green-500",
  draft: "bg-amber-500",
  rendering: "bg-yellow-500",
  failed: "bg-red-500"
};

const Dashboard = () => {
  const navigate = useNavigate();
  const user = useAuth();
  const userId = user?.session?.user?.id;
  const dispatch = useDispatch();
  const videos = useSelector((state: RootState) => state.dashboardVideos.videos);
  const [getVideos, { loading }] = useLazyQuery(GET_USER);
  const [markVideoAsDownloaded] = useMutation(MARK_VIDEO_AS_DOWNLOADED);
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);
  const isMobile = useIsMobile();

  // Function to handle video download properly
  const handleDownloadVideo = async (id: string, videoUrl: string, projectName: string) => {
    try {
      toast.info("Starting download...");

      const response = await fetch(videoUrl);

      if (!response.ok) {
        throw new Error(`Failed to download: ${response.statusText}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');

      downloadLink.href = blobUrl;
      downloadLink.download = `${projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_video.mp4`;

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Mark video as downloaded
      await markVideoAsDownloaded({ variables: { video_id: id } });

      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

      toast.success("Download started!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download video. Try again or contact support.");
    }
  };

  useEffect(() => {
    const fetchVideos = async () => {
      if (!userId) return;

      try {
        const { data: videosData } = await getVideos({ variables: { id: userId } });

        dispatch(setVideos(videosData?.user?.videos || []));
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchVideos();
  }, [userId]);

  // Transform videos to the new VideoProject format
  const videoProjects = videos.map(video => ({
    id: video.id,
    property: video.display_title ?? `Video #${video.id}`,
    thumbnail: video.thumbnail_url || 'https://placehold.co/600x400?text=No+Thumbnail',
    status: video.status || 'draft',
    progress: video.status === 'completed' ? 100 : 30,
    dateOrdered: new Date(video.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    estimatedDelivery: new Date(new Date(video.created_at).getTime() + 2*24*60*60*1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    actor: video.avatar?.name || 'Unknown Actor',
    actorImage: video.avatar?.preview_image_url,
    scriptStyle: video.avatar?.description || 'Standard',
    videoUrl: video.url,
    editAttemptsUsed: video.edit_attempts_used
  }));

  const sortedProjects = [...videoProjects].sort((a, b) => {
    const dateA = new Date(a.dateOrdered);
    const dateB = new Date(b.dateOrdered);
    return dateB.getTime() - dateA.getTime();
  });

  const handlePreviewVideo = (videoUrl: string) => {
    setPreviewVideo(videoUrl);
    console.log(videoUrl);
  };

  const handleEditVideo = (id: string) => {
    navigate(`/edit-video/${id}`);
  };

  return (
    <Layout>
      <PageTransition>
        <Header />

        <motion.div
          className="space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="flex justify-between items-center mb-2 px-[10px]">
            <h2 className="text-2xl font-bold tracking-tight">Your Videos</h2>

            {isMobile ? (
              <div className="fixed bottom-6 right-6 z-10">
                <ActionButton to="/" variant="floating" fullWidth={false}>
                  <span className="font-semibold">
                    <Video className="mr-1.5 h-4 w-4 inline" />
                    Create Video
                  </span>
                </ActionButton>
              </div>
            ) : (
              <ActionButton to="/">
                <Video className="mr-1.5 h-4 w-4" />
                Create New Video
              </ActionButton>
            )}
          </div>

          <div className="glass rounded-xl overflow-hidden">
            <div className="p-4">
              {loading && !sortedProjects.length ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Loading your videos...</p>
                </div>
              ) : sortedProjects.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No videos found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sortedProjects.map(project => {
                    const truncatedTitle = project.property;

                    return (
                      <motion.div
                        key={project.id}
                        className="border rounded-lg overflow-hidden bg-card card-hover"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ y: -2 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="md:grid md:grid-cols-[30%_70%]">
                          <div
                            className="relative h-80 md:h-auto cursor-pointer"
                            onClick={(e) => { if (project.videoUrl) handlePreviewVideo(project.videoUrl) }}
                          >
                            <img
                              src={project.thumbnail}
                              alt={project.property}
                              className="w-full h-full object-cover absolute"
                            />

                            {project.videoUrl && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity">
                                <ActionButton variant="play">
                                  <span className="sr-only">Play Video</span>
                                </ActionButton>
                              </div>
                            )}

                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 md:hidden">
                              <div className="text-white font-medium">{truncatedTitle}</div>
                            </div>
                          </div>

                          <div className="p-4">
                            <div className="justify-between items-start">
                              <div>
                                <hgroup className="mb-2 w-full">
                                  <h3 className="font-medium text-lg hidden md:block truncate" data-video-id={project.id}>{truncatedTitle}</h3>
                                  <h6 className="text-muted-foreground m-0 text-xs align-right">Created: {project.dateOrdered} | ID: {project.id}</h6>
                                </hgroup>

                                <div className="flex items-center mt-1 space-x-2">
                                  <span className={`inline-flex h-2.5 w-2.5 rounded-full ${statusColors[project.status as keyof typeof statusColors] || "bg-gray-500"}`}></span>
                                  <span className="text-sm text-muted-foreground">
                                    {statusLabels[project.status as keyof typeof statusLabels] || project.status}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Actor information with image */}
                            <div className="mt-3 flex items-center">
                              <div className="flex items-center space-x-3">
                                <Avatar className="h-8 w-8 border border-primary/10">
                                  <AvatarImage src={project.actorImage} alt={project.actor} />
                                  <AvatarFallback className="bg-primary/5">
                                    <User className="h-4 w-4 text-primary/70" />
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <span className="text-sm font-medium">{project.actor}</span>
                                </div>
                              </div>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                              {project.status === "completed" && (
                                <>
                                  <button
                                    onClick={() => handleDownloadVideo(project.id, project.videoUrl!, project.property)}
                                    className="bg-primary text-primary-foreground py-1.5 px-3 rounded-md text-sm font-medium inline-flex items-center"
                                  >
                                    <Download className="mr-1.5 h-4 w-4" />
                                    Download
                                  </button>

                                  <button
                                    className="bg-secondary text-secondary-foreground py-1.5 px-3 rounded-md text-sm font-medium inline-flex items-center"
                                    onClick={() => handlePreviewVideo(project.videoUrl!)}
                                  >
                                    <Play className="mr-1.5 h-4 w-4" />
                                    Preview
                                  </button>

                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          className="border border-input bg-background hover:bg-accent hover:text-accent-foreground py-1.5 px-3 rounded-md text-sm font-medium inline-flex items-center disabled:opacity-50 disabled:hover:bg-background"
                                          onClick={() => handleEditVideo(project.id)}
                                          disabled={project.editAttemptsUsed !== 0}
                                        >
                                          Edit
                                        </button>
                                      </TooltipTrigger>
                                      {project.editAttemptsUsed !== 0 && (
                                        <TooltipContent>
                                          <p>You already edited this video</p>
                                        </TooltipContent>
                                      )}
                                    </Tooltip>
                                  </TooltipProvider>
                                </>
                              )}

                              {project.status === "failed" && (
                                <div className="text-sm text-muted-foreground">
                                  {isMobile ? (
                                    <a
                                      href={`sms:+16285612205?body=Hi%2C%20I%20am%20creating%20my%20Clips%20video%20and%20I%20need%20help.`}
                                      className="border border-input bg-background hover:bg-accent hover:text-accent-foreground py-1.5 px-3 rounded-md text-sm font-medium inline-flex items-center"
                                    >
                                      <Headset className="mr-1.5 h-4 w-4" />
                                      Contact Support (628) 561-2205
                                    </a>
                                  ) : (
                                    <a
                                      href="mailto:support@keven.ai?subject=Video%20Creation%20Issue&body=Please%20describe%20the%20issue%20you%20are%20experiencing%20with%20the%20video%20creation%20process."
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="border border-input bg-background hover:bg-accent hover:text-accent-foreground py-1.5 px-3 rounded-md text-sm font-medium inline-flex items-center"
                                    >
                                      <Headset className="mr-1.5 h-4 w-4" />
                                      Contact Support (628) 561-2205
                                    </a>
                                  )}
                                </div>
                              )}

                              {project.status === "rendering" && (
                                <div className="text-sm text-muted-foreground">
                                  Video is being created...
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Video Preview Modal */}
        {previewVideo && (
          <VideoModal
            isOpen={Boolean(previewVideo)}
            onClose={() => setPreviewVideo(null)}
            videoUrl={previewVideo}
          />
        )}
      </PageTransition>
    </Layout>
  );
};

export default Dashboard;
